import { useEffect } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'resources/images/close-circle.svg';
import { SongStatusType } from 'types/song';
import { useIsMobile } from 'app/utils/hooks';
import { useAppDispatch, useAppSelector } from 'app/store';
import { isExtendedPlayer } from 'app/store/AudioPlayer/selectors';
import { setExtendedAction } from 'app/store/AudioPlayer/slice';
import { routes } from 'app/routing/routesConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import { AudioPlayerDetails } from './AudioPlayerDetails';
import { AudioPlayerControls } from './AudioPlayerControls';
import { WAVE_HEIGHT, useWaveSurferPlayer } from './hooks';
import { audioPlayerSx } from './styles';
import { AudioPlayerComponentProps } from './types';

export const AudioPlayerComponent = ({
  handleClose,
  setIsPlaying,
  isPlaying,
  setIsPlayerReady,
  isOpen,
  transitionType = 'persistent',
  setIsWaveformOpen,
  isWaveformOpen,
  playMode,
  isPlayerReady,
  queue,
  queueIndex,
  isPlayerExpandable,
  songCover,
  images,
}: AudioPlayerComponentProps) => {
  const dispatch = useAppDispatch();
  const extendedPlayer = useAppSelector(isExtendedPlayer);
  const isMobile = useIsMobile(false);

  const currentAudio = queue[queueIndex];
  const player = useWaveSurferPlayer();

  const styles = audioPlayerSx({
    isOpen,
    transitionType,
    waveFormEnabled: isWaveformOpen && !isMobile,
    extendedPlayer,
    isMobile,
  });

  useEffect(() => {
    if (!isOpen && isPlayerReady) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isLoading = currentAudio?.loading || !isPlayerReady || false;

  useEffect(() => {
    if (isMobile) {
      setIsWaveformOpen(false);
    }
  }, [isMobile, dispatch, setIsWaveformOpen]);

  useEffect(() => {
    if (!isMobile && extendedPlayer) {
      dispatch(setExtendedAction(false));
    }
    player?.setOptions?.({
      height: extendedPlayer ? 0 : WAVE_HEIGHT,
    });
  }, [dispatch, extendedPlayer, isMobile, player]);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const goBack = [routes.playerLyrics, routes.playerPlaylist].some(
    ({ path }) => path === pathname
  );

  return (
    <>
      <Box sx={styles.extended.overlay} />
      <Box
        data-testid={`audio-player-${isOpen ? 'visible' : 'hidden'}`}
        sx={extendedPlayer ? styles.extended.base : styles.base}
      >
        <Box sx={styles.container}>
          <AudioPlayerDetails
            audioTitle={currentAudio?.audioTitle}
            songGuid={currentAudio?.versionSongGuid || currentAudio?.parentGuid}
            songStatus={
              songCover ? SongStatusType.IN_CREATION : currentAudio?.songStatus
            }
            songTitle={currentAudio?.songTitle}
            isPlaylistMode={!songCover && playMode === 'playlist'}
            image={images?.find((el) => el.name === 'playlist-tiny')?.href}
          />

          {extendedPlayer && (
            <Box mt="52px" mb={8}>
              <Text
                sx={styles.extended.audiotitle}
                title={currentAudio?.audioTitle}
              >
                {currentAudio?.audioTitle}
              </Text>
              <Text
                sx={styles.extended.songTitle}
                title={currentAudio?.songTitle}
              >
                {currentAudio?.songTitle}
              </Text>
            </Box>
          )}

          <AudioPlayerControls
            isLoading={isLoading}
            isPlaying={isPlaying}
            src={currentAudio?.audioSrc || ''}
            isError={currentAudio?.isError || false}
            isPlaylistMode={playMode === 'playlist'}
            player={player}
            isOpen={isOpen}
            setIsPlayerReady={setIsPlayerReady}
            setIsPlaying={setIsPlaying}
            isPlayerExpandable={isPlayerExpandable}
          />
          <Flex sx={styles.closeChevronContainer}>
            {isPlayerExpandable && (
              <Icon
                data-testid="audio-close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setExtendedAction(false));
                  if (goBack) {
                    navigate(-1);
                  }
                  handleClose();
                }}
                sx={styles.closeButton}
                as={CloseIcon}
              />
            )}
          </Flex>
        </Box>
      </Box>
    </>
  );
};
